import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Home from '@mui/icons-material/esm/Home';
import Engineering from '@mui/icons-material/esm/Engineering';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import Warning from '@mui/icons-material/esm/Warning';
import BuildIcon from '@mui/icons-material/esm/Build';
import HourglassEmpty from '@mui/icons-material/esm/HourglassEmpty';
import { useSelector } from 'react-redux';

import { KPICard, DataTable, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import unitService, {
  UnitStatusMap,
  UnitStatusRow,
  DerivedConstructionStatus,
} from '../../services/api/unitService';

const STATUS_COLOR: Record<DerivedConstructionStatus, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  planned: 'default',
  in_progress: 'primary',
  blocked: 'error',
  remedial: 'warning',
  completed: 'info',
  handover_ready: 'success',
};

const STATUS_LABEL: Record<DerivedConstructionStatus, string> = {
  planned: 'Planned',
  in_progress: 'In Progress',
  blocked: 'Blocked / At Risk',
  remedial: 'Remedial',
  completed: 'Completed',
  handover_ready: 'Handover Ready',
};

export default function UnitStatusPage() {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<UnitStatusMap | null>(null);
  const [phaseFilter, setPhaseFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<DerivedConstructionStatus | 'all'>('all');
  const [selectedUnit, setSelectedUnit] = useState<UnitStatusRow | null>(null);

  useEffect(() => {
    if (!activeProjectId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await unitService.getStatusMap(activeProjectId);
        if (!cancelled) setData(result);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load unit status map');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeProjectId]);

  const phases = useMemo(() => {
    if (!data) return [];
    const seen = new Map<string, string>();
    for (const u of data.units) {
      if (!seen.has(u.phaseId)) seen.set(u.phaseId, u.phaseName);
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [data]);

  const filteredUnits = useMemo(() => {
    if (!data) return [];
    return data.units.filter((u) => {
      if (phaseFilter !== 'all' && u.phaseId !== phaseFilter) return false;
      if (statusFilter !== 'all' && u.constructionStatus !== statusFilter) return false;
      return true;
    });
  }, [data, phaseFilter, statusFilter]);

  if (!activeProjectId) {
    return (
      <Box>
        <PageHeader title="Unit Status" />
        <Alert severity="info">Select a project to view unit status.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <PageHeader title="Unit Status" />
        <Alert severity="error">{error || 'No data available'}</Alert>
      </Box>
    );
  }

  const columns: Column<UnitStatusRow>[] = [
    { id: 'code', label: 'Unit', minWidth: 80 },
    { id: 'name', label: 'Name', minWidth: 140 },
    { id: 'buildingCode', label: 'Building', minWidth: 100 },
    { id: 'blockCode', label: 'Block', minWidth: 90 },
    {
      id: 'constructionStatus',
      label: 'Construction',
      minWidth: 140,
      format: (value: DerivedConstructionStatus) => (
        <Chip size="small" label={STATUS_LABEL[value]} color={STATUS_COLOR[value]} />
      ),
    },
    {
      id: 'completionPercentage',
      label: '% Complete',
      minWidth: 90,
      align: 'right',
      format: (value: number) => `${value}%`,
    },
    {
      id: 'openDefectCount',
      label: 'Open Defects',
      minWidth: 110,
      align: 'right',
      format: (value: number) => (value > 0 ? (
        <Typography variant="body2" fontWeight="bold" color="warning.main">{value}</Typography>
      ) : '—'),
    },
    {
      id: 'saleStatus',
      label: 'Sale',
      minWidth: 110,
      format: (value: string) => value.replace(/_/g, ' '),
    },
    {
      id: 'targetHandoverDate',
      label: 'Target Handover',
      minWidth: 130,
      format: (value: string | null) => (value ? new Date(value).toLocaleDateString() : '—'),
    },
  ];

  return (
    <Box>
      <PageHeader title="Unit Status" subtitle={`${data.totals.unitCount} units`} />

      {/* Status summary KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <KPICard title="Planned" value={`${data.totals.byConstructionStatus.planned}`} icon={<Home />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KPICard title="In Progress" value={`${data.totals.byConstructionStatus.in_progress}`} icon={<Engineering />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KPICard title="Blocked / At Risk" value={`${data.totals.byConstructionStatus.blocked}`} icon={<Warning />} color={data.totals.byConstructionStatus.blocked > 0 ? 'error' : 'success'} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KPICard title="Remedial" value={`${data.totals.byConstructionStatus.remedial}`} icon={<BuildIcon />} color={data.totals.byConstructionStatus.remedial > 0 ? 'warning' : 'success'} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KPICard title="Completed" value={`${data.totals.byConstructionStatus.completed}`} icon={<CheckCircle />} color="info" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KPICard title="Handover Ready" value={`${data.totals.byConstructionStatus.handover_ready}`} icon={<HourglassEmpty />} color="success" />
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Phase</InputLabel>
          <Select value={phaseFilter} label="Phase" onChange={(e) => setPhaseFilter(e.target.value)}>
            <MenuItem value="all">All phases</MenuItem>
            {phases.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Construction status</InputLabel>
          <Select value={statusFilter} label="Construction status" onChange={(e) => setStatusFilter(e.target.value as any)}>
            <MenuItem value="all">All statuses</MenuItem>
            {(Object.keys(STATUS_LABEL) as DerivedConstructionStatus[]).map((s) => (
              <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Units table */}
      <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Units ({filteredUnits.length})
        </Typography>
        {filteredUnits.length === 0 ? (
          <Alert severity="info">No units match the current filters.</Alert>
        ) : (
          <DataTable
            columns={columns}
            data={filteredUnits}
            pagination
            searchable
            onRowClick={(row) => setSelectedUnit(row)}
          />
        )}
      </Box>

      {/* Detail drawer */}
      <Drawer anchor="right" open={!!selectedUnit} onClose={() => setSelectedUnit(null)}>
        {selectedUnit && (
          <Box sx={{ width: 360, p: 3 }}>
            <Typography variant="h6" fontWeight="bold">{selectedUnit.code} — {selectedUnit.name}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {selectedUnit.phaseName} · {selectedUnit.blockCode} · {selectedUnit.buildingCode}
            </Typography>
            <Chip
              size="small"
              label={STATUS_LABEL[selectedUnit.constructionStatus]}
              color={STATUS_COLOR[selectedUnit.constructionStatus]}
              sx={{ mt: 1, mb: 2 }}
            />
            <Divider />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Construction</Typography>
              <Typography variant="body2">% Complete: <strong>{selectedUnit.completionPercentage}%</strong></Typography>
              <Typography variant="body2">Tasks: {selectedUnit.taskCount} total / {selectedUnit.taskCountActive} active / {selectedUnit.taskCountBlocked} blocked</Typography>
              <Typography variant="body2">Open defects: {selectedUnit.openDefectCount}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2">Sale</Typography>
              <Typography variant="body2">Status: <strong>{selectedUnit.saleStatus.replace(/_/g, ' ')}</strong></Typography>
              {selectedUnit.saleContractNumber && (
                <>
                  <Typography variant="body2">Contract: {selectedUnit.saleContractNumber}</Typography>
                  <Typography variant="body2">Buyer: {selectedUnit.saleContractBuyerName || '—'}</Typography>
                  <Typography variant="body2">Contract status: {selectedUnit.saleContractStatus || '—'}</Typography>
                </>
              )}
              <Typography variant="body2" sx={{ mt: 1 }}>
                Target handover: {selectedUnit.targetHandoverDate ? new Date(selectedUnit.targetHandoverDate).toLocaleDateString() : '—'}
              </Typography>
              <Typography variant="body2">
                Actual handover: {selectedUnit.actualHandoverDate ? new Date(selectedUnit.actualHandoverDate).toLocaleDateString() : '—'}
              </Typography>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
